import "vanilla-cookieconsent/dist/cookieconsent.css";
import * as CookieConsent from "vanilla-cookieconsent";

export default defineNuxtPlugin((nuxtApp) => {
   // Provide CookieConsent to the app
   nuxtApp.provide("CookieConsent", CookieConsent);
   const { logInfo, logWarn } = useDebugger();
   // Only run configuration on client side
   if (process.server) return;

   const { updateConsent } = useGTM();

   // Function to update GTM consent based on cookie consent choices
   const updateGTMConsent = (cookie: any) => {
      if (!process.client) return;

      // logInfo("🍪 Updating GTM consent with cookie data:", cookie);

      const consentUpdate: any = {
         functionality_storage: "granted", // Always granted for necessary cookies
         security_storage: "granted",
      };

      // Check if analytics category is accepted
      // Cookie consent libraries use different formats, so check multiple possible structures
      const analyticsAccepted =
         cookie?.categories?.includes("analytics") ||
         cookie?.level?.includes("analytics") ||
         cookie?.analytics === true ||
         (Array.isArray(cookie) && cookie.includes("analytics"));

      if (analyticsAccepted) {
         consentUpdate.analytics_storage = "granted";
      } else {
         consentUpdate.analytics_storage = "denied";
      }

      // Check if ads category is accepted (if you have it)
      const adsAccepted =
         cookie?.categories?.includes("ads") ||
         cookie?.level?.includes("ads") ||
         cookie?.ads === true ||
         (Array.isArray(cookie) && cookie.includes("ads"));

      if (adsAccepted) {
         consentUpdate.ad_storage = "granted";
         consentUpdate.ad_user_data = "granted";
         consentUpdate.ad_personalization = "granted";
      } else {
         consentUpdate.ad_storage = "denied";
         consentUpdate.ad_user_data = "denied";
         consentUpdate.ad_personalization = "denied";
      }

      // Update GTM consent using our composable
      updateConsent(consentUpdate);
   };

   // Test function to manually check current consent state
   const testConsentState = () => {
      if (process.client && CookieConsent) {
         const currentCookie = CookieConsent.getCookie();
         // logInfo("🔍 Current consent state:", currentCookie);
         updateGTMConsent(currentCookie);
      }
   };

   // Expose for debugging in browser console
   if (process.client) {
      (window as any).testConsentState = testConsentState;
   }

   // Initialize cookie consent when app is mounted
   nuxtApp.hook("app:mounted", () => {
      if (!CookieConsent) {
         // logWarn("CookieConsent not available");
         return;
      }

      /**
       * All config options available here:
       * https://cookieconsent.orestbida.com/reference/configuration-reference.html
       */
      CookieConsent.run({
         // root: 'body',
         // autoShow: true,
         disablePageInteraction: false, // Allow page interaction when modal is open
         // hideFromBots: true,
         // mode: 'opt-in',
         // revision: 0,

         cookie: {
            name: "cc_cookie",
         },

         // https://cookieconsent.orestbida.com/reference/configuration-reference.html#guioptions
         guiOptions: {
            consentModal: {
               layout: "box inline",
               position: "bottom right",
               equalWeightButtons: true,
               flipButtons: false,
            },
            preferencesModal: {
               layout: "box",
               equalWeightButtons: true,
               flipButtons: false,
            },
         },

         onFirstConsent: ({ cookie }: { cookie: any }) => {
            // logInfo("🎉 First consent given:", cookie);
            updateGTMConsent(cookie);
         },

         onConsent: ({ cookie }: { cookie: any }) => {
            // logInfo("✅ Consent updated:", cookie);
            updateGTMConsent(cookie);
         },

         onChange: ({
            changedCategories,
            changedServices,
         }: {
            changedCategories: any;
            changedServices: any;
         }) => {
            logInfo(
               "🔄 Cookie preferences changed!",
               changedCategories,
               changedServices,
            );

            // Get the latest cookie state - this should contain the updated preferences
            const cookie = CookieConsent.getCookie();
            logInfo("📋 Current cookie state after change:", cookie);

            // Update GTM consent when categories change
            updateGTMConsent(cookie);

            // Alternative: We could also use the changedCategories directly
            // if the cookie state isn't immediately updated
            if (changedCategories) {
               logInfo(
                  "🏷️ Using changed categories for GTM update:",
                  changedCategories,
               );
               updateGTMConsent(changedCategories);
            }
         },

         // onModalShow: ({ modalName }: { modalName: string }) => {},
         // onModalHide: ({ modalName }: { modalName: string }) => {},

         categories: {
            necessary: {
               enabled: true, // this category is enabled by default
               readOnly: true, // this category cannot be disabled
            },
            analytics: {
               autoClear: {
                  cookies: [
                     {
                        name: /^_ga/, // regex: match all cookies starting with '_ga'
                     },
                     {
                        name: "_gid", // string: exact cookie name
                     },
                  ],
               },

               // https://cookieconsent.orestbida.com/reference/configuration-reference.html#category-services
               services: {
                  gtm: {
                     label: "Google Tag Manager",
                     onAccept: () => {
                        // console.log("GTM service accepted");
                     },
                     onReject: () => {
                        // console.log("GTM service rejected");
                     },
                  },
                  ga: {
                     label: "Google Analytics",
                     onAccept: () => {
                        // console.log("GA service accepted");
                     },
                     onReject: () => {
                        // console.log("GA service rejected");
                     },
                  },
               },
            },
            ads: {},
         },

         language: {
            default: "en",
            translations: {
               en: {
                  consentModal: {
                     title: "We use cookies",
                     description:
                        'We use cookies, pixels, and similar technologies for many different site operations such as improving website experiences, analytics, remembering preferences or settings, and marketing and advertising. Some of these technologies transmit information to third parties (such as information about how you interact with our website). By clicking "Accept All" you consent to our use of all cookies and similar technologies. By clicking "Reject All" you agree that our website will only deploy strictly necessary cookies. For more information, please see our <a href="https://www.juilliard.edu/juilliard-website-privacy-policy-your-privacy-rights">Privacy Policy</a>',
                     acceptAllBtn: "Accept all",
                     acceptNecessaryBtn: "Reject all",
                     showPreferencesBtn: "Manage Individual preferences",
                     footer: ``,
                  },
                  preferencesModal: {
                     title: "Manage cookie preferences",
                     acceptAllBtn: "Accept all",
                     acceptNecessaryBtn: "Reject all",
                     savePreferencesBtn: "Accept current selection",
                     closeIconLabel: "Close modal",
                     serviceCounterLabel: "Service|Services",
                     sections: [
                        {
                           title: "Cookie Preferences",
                           description:
                              'We use cookies, pixels, and similar technologies for many different site operations such as improving website experiences, analytics, remembering preferences or settings, and marketing and advertising. Some of these technologies transmit information to third parties (such as information about how you interact with our website). By clicking "Accept All" you consent to our use of all cookies and similar technologies. By clicking "Reject All" you agree that our website will only deploy strictly necessary cookies. For more information, please see our <a href="https://www.juilliard.edu/juilliard-website-privacy-policy-your-privacy-rights">Privacy Policy</a>',
                        },
                        {
                           title: "Strictly Necessary",
                           description:
                              "These cookies are essential for the proper functioning of the website and cannot be disabled.",
                           linkedCategory: "necessary",
                           cookieTable: {
                              caption: "Cookie table",
                              headers: {
                                 name: "Cookie",
                                 domain: "Domain",
                                 desc: "Description",
                              },
                              body: [
                                 {
                                    name: "cc_cookie",
                                    domain:
                                       typeof window !== "undefined"
                                          ? location.hostname
                                          : "",
                                    duration: "Persistent",
                                    desc: "Stores the user's cookie consent preferences.",
                                 },
                                 {
                                    name: "cookieconsent_status",
                                    domain:
                                       typeof window !== "undefined"
                                          ? location.hostname
                                          : "",
                                    duration: "Persistent",
                                    desc: "Stores the user's cookie consent status.",
                                 },
                              ],
                           },
                        },
                        {
                           title: "Performance and Analytics",
                           description:
                              "These cookies collect information about how you use our website. All of the data is anonymized and cannot be used to identify you.",
                           linkedCategory: "analytics",
                           cookieTable: {
                              caption: "Cookie table",
                              headers: {
                                 name: "Cookie",
                                 domain: "Domain",
                                 desc: "Description",
                              },
                              body: [
                                 {
                                    name: "wistia-video-progress-#",
                                    domain:
                                       typeof window !== "undefined"
                                          ? location.hostname
                                          : "",
                                    duration: "Persistent",
                                    desc: "Contains a timestamp for the website's video-content. This allows the user to resume watching without having to start over, if the user leaves the video or website.",
                                 },
                                 {
                                    name: "apc_local_id",
                                    domain: "Appcues",
                                    duration: "Persistent",
                                    desc: "Used in context with the VR-content on website. Collects data on who, what and when the content has been viewed for statistical purposes.",
                                 },
                                 {
                                    name: "apc_user",
                                    domain: "Appcues",
                                    duration: "Session",
                                    desc: "Used in context with the VR-content on website. Collects data on who, what and when the content has been viewed for statistical purposes.",
                                 },
                                 {
                                    name: "mp_#_mixpanel",
                                    domain: "Mixpanel",
                                    duration: "Session",
                                    desc: "Used in context with the Mixpanel analytics service. Collects data on user behavior and interaction, used for statistical purposes.",
                                 },
                                 {
                                    name: "sentryReplaySession",
                                    domain: "Sentry",
                                    duration: "Session",
                                    desc: "Used in context with the Sentry error logging service. Collects data on user behavior and interaction, used for statistical purposes.",
                                 },
                                 {
                                    name: "_ga",
                                    domain:
                                       typeof window !== "undefined"
                                          ? location.hostname
                                          : "",
                                    desc: "Stores the unique identifier used by Google Analytics to track user interactions.",
                                 },
                                 {
                                    name: "_gid",
                                    domain:
                                       typeof window !== "undefined"
                                          ? location.hostname
                                          : "",
                                    desc: "Stores the unique identifier used by Google Analytics to track user interactions.",
                                 },
                                 {
                                    name: "_gtag",
                                    domain: "Google",
                                    duration: "Session",
                                    desc: "Used by Google Tag Manager to track user interactions and page views.",
                                 },
                                 {
                                    name: "_gcl_*",
                                    domain: "Google",
                                    duration: "90 days",
                                    desc: "Used by Google Analytics to store and track conversions.",
                                 },
                              ],
                           },
                        },
                     ],
                  },
               },
            },
         },
      });

      console.log("🍪 Cookie consent initialized");
   });
});
